import {
  Box,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  Chip,
  Checkbox,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import React, { FC } from "react";
import { TABLE_HEADER_ARRAY } from "../../utils/Types";
import CheckBox from "@mui/icons-material/CheckBox";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { STATUS } from "../../utils/Constants";
import "./DynamicTableBody.css";
import trim from "../../helpers/trim";
import formatDateTime from "../../helpers/formatDateTime";
import { CASE_ID_COL_WIDTH, CHECKBOX_COL_WIDTH } from "./DynamicTableHead";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

export type DynamicTableBodyProps = {
  headersArr?: TABLE_HEADER_ARRAY;
  data?: Array<any>;
  onRowClick?: (tableItem: any) => void;
  transparentRows?: boolean;
  enableRowSelection?: boolean;
  selectedRows?: any[];
  onCheck?: (tableItem: any) => void;
  enableRowExpansion?: boolean;
  expandedIds?: Set<string | number>;
  toggleExpand?: (row: any) => void;
  getRowId?: (row: any) => string | number;
  isRowExpandable?: (row: any) => boolean;
  renderExpandedRow?: (row: any) => React.ReactNode;
};

const DynamicTableBody: FC<DynamicTableBodyProps> = ({
  headersArr = [],
  data = [],
  onRowClick,
  transparentRows = false,
  enableRowSelection = false,
  selectedRows = [],
  onCheck = () => {},
  enableRowExpansion = false,
  expandedIds = new Set(),
  toggleExpand,
  getRowId,
  isRowExpandable,
  renderExpandedRow,
}) => {
  const { palette } = useTheme();

  const renderObject = (item: any, key: any) => {
    const title = trim(item[key]?.title);
    const subtitle = item[key]?.subtitle;
    const extraLine = item[key]?.extraLine;
    const truncate = (value: string, max = 30) =>
      value.length > max ? `${value.slice(0, max)}…` : value;
    return (
      <div>
        <Typography color={item[key]?.titleColor}>
          {title?.length > 0 ? (
            <Tooltip title={title} disableTouchListener disableFocusListener>
              <span>{truncate(title)}</span>
            </Tooltip>
          ) : (
            "--"
          )}
        </Typography>
        {item[key]?.subtitle && (
          <>
            <br />
            <Tooltip
              title={String(subtitle)}
              disableTouchListener
              disableFocusListener
            >
              <small>{truncate(String(subtitle))}</small>
            </Tooltip>
          </>
        )}
        {extraLine && (
          <>
            <br />
            <small>{extraLine}</small>
          </>
        )}
      </div>
    );
  };

  const renderEmrStatus = (item: any) => {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Box
          width={18}
          height={18}
          bgcolor={
            item.emrStatus === true || item.emrStatus === "true"
              ? "custom.foreground.progress"
              : "custom.foreground.attention"
          }
          borderRadius="50%"
        />
      </div>
    );
  };

  const renderCodes = (item: any) => {
    return item.codes ? (
      <CheckBox
        sx={{
          width: 20,
          height: 20,
          color: "custom.background.brand",
        }}
      />
    ) : null;
  };

  const renderStatus = (item: any, key: any) => {
    let bgColor;
    let iconColor;
    let StatusIcon;

    switch (item[key]) {
      case STATUS.DEACTIVATED:
        bgColor = "custom.background.deactivated";
        iconColor = "error";
        StatusIcon = HighlightOffIcon;
        break;
      case STATUS.PENDING:
      case STATUS.INCOMPLETE:
        bgColor = "custom.background.pending";
        iconColor = "warning";
        StatusIcon = HourglassEmptyRoundedIcon;
        break;
      default:
        bgColor = "custom.background.activated";
        iconColor = "success";
        StatusIcon = CheckCircleOutlineRoundedIcon;
    }

    return (
      <Chip
        icon={
          <StatusIcon
            color={iconColor as any}
            sx={{
              fontSize: 20,
            }}
          />
        }
        label={item[key]}
        sx={{
          fontSize: 16,
          backgroundColor: bgColor,
          borderRadius: 2,
        }}
      />
    );
  };

  const renderCheckbox = (item: any, key: any) => {
    return (
      <Checkbox
        key={key}
        size="medium"
        checked={item[key]}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          onCheck(item);
        }}
        sx={{
          "&.Mui-checked": {
            color: "custom.background.brand",
          },
        }}
      />
    );
  };
  const renderExpandCell = (row: any) => {
    if (!enableRowExpansion || !toggleExpand || !getRowId || !renderExpandedRow)
      return null;

    const expandable = isRowExpandable ? isRowExpandable(row) : true;
    if (!expandable) return null;

    const id = getRowId(row);
    const isExpanded = expandedIds.has(id);

    return (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          toggleExpand(row);
        }}
      >
        {isExpanded ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
    );
  };

  const renderArray = (array: string[], maxLength = 30) => {
    const full = array.length > 0 ? array.map((item) => item).join(", ") : "";
    const truncate = (value: string, max = maxLength) =>
      value.length > max ? `${value.slice(0, max)}…` : value;
    return (
      <>
        {full?.length > 0 ? (
          <Tooltip title={full} disableTouchListener disableFocusListener>
            <span>{truncate(full)}</span>
          </Tooltip>
        ) : (
          "--"
        )}
      </>
    );
  };

  const truncateText = (v: string, max = 50) => (v.length > max ? `${v.slice(0, max)}…` : v);

const isTotalBoldKey = (row: any, key: string) =>
  !!row?._isTotalRow &&
  ["interval", "prediction", "historicalAvg", "delta", "confidence"].includes(key);

const renderTimestamp = (value: any) => (
  <>{trim(value)?.length > 0 ? formatDateTime(value) : "--"}</>
);

const renderTextCell = (item: any, key: string) => {
  const value = trim(item[key]);

  if (!(typeof value === "string" && value.length > 0)) {
    return <>{value?.length > 0 ? value : "--"}</>;
  }

  const content = isTotalBoldKey(item, key) ? (
    <Typography component="span" fontWeight={600}>
      {truncateText(value)}
    </Typography>
  ) : (
    <span>{truncateText(value)}</span>
  );

  return (
    <Tooltip title={value} disableTouchListener disableFocusListener>
      {content}
    </Tooltip>
  );
};

 const renderCellContent = (item: any, key: string, type?: string, arrayTruncateLength?: number) => {
  const cellValue = item[key];

  // key-based handlers
  const keyRenderers: Record<string, () => React.ReactNode> = {
    expand: () => renderExpandCell(item),
    emrStatus: () => renderEmrStatus(item),
    codes: () => renderCodes(item),
    status: () => renderStatus(item, key),
  };

  const keyRenderer = keyRenderers[key];
  if (keyRenderer) return keyRenderer();

  // direct react element
  if (React.isValidElement(cellValue)) return cellValue;

  // value-shape handlers
  if (Array.isArray(cellValue)) return renderArray(cellValue, arrayTruncateLength);
  if (cellValue && typeof cellValue === "object") return renderObject(item, key);

  // type-based handlers
  if (type === "checkbox") return renderCheckbox(item, key);
  if (type === "timestamp") return renderTimestamp(cellValue);

  // fallback text renderer
  return renderTextCell(item, key);
};


  return (
    <TableBody>
      {data?.map((item) => {
        const rowId = getRowId ? getRowId(item) : (item?.id ?? item?.caseId);
        const expandable = isRowExpandable ? isRowExpandable(item) : true;
        const isExpanded = enableRowExpansion && expandedIds.has(rowId);

        return (
          <React.Fragment key={String(rowId)}>
            <TableRow
              sx={
                transparentRows
                  ? {
                      backgroundColor: "transparent",
                      "&:nth-of-type(even)": { backgroundColor: "transparent" },
                      "& td, & th": {
                        border: "none",
                        backgroundColor: "transparent",
                      },
                      ...(onRowClick && { cursor: "pointer" }),
                    }
                  : {
                      backgroundColor: palette.custom.background.primary,
                      "&:nth-of-type(even)": {
                        backgroundColor: palette.custom.background.navigation,
                      },
                      "&:last-child td, &:last-child th": { border: 0 },
                      ...(onRowClick && { cursor: "pointer" }),
                    }
              }
              className={transparentRows ? "transparent-table-row" : ""}
              onClick={() => onRowClick?.(item)}
            >
              {enableRowSelection && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    position: "sticky",
                    left: 0,
                    width: CHECKBOX_COL_WIDTH,
                    zIndex: 2,
                    backgroundColor: palette.custom.background.primary,
                  }}
                >
                  <Checkbox
                    checked={selectedRows.some((r) => r.caseId === item.caseId)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onCheck(item)}
                    sx={{
                      "&.Mui-checked": {
                        color: "custom.background.brand",
                      },
                    }}
                  />
                </TableCell>
              )}

              {headersArr?.map((headerItem) => {
                const { key, position = "left", type, arrayTruncateLength } = headerItem ?? {};
                const isCaseId = enableRowSelection && key === "caseId";
                const leftOffset = isCaseId ? CHECKBOX_COL_WIDTH : undefined;
                const cellPosition = isCaseId ? "sticky" : "static";
                const cellZIndex = isCaseId ? 1 : 0;
                const minCellWidth = isCaseId
                  ? CASE_ID_COL_WIDTH
                  : headerItem.width;

                return (
                  <TableCell
                    key={`${key}-${String(rowId)}`}
                    align={position}
                    sx={{
                      fontSize: 16,
                      fontWeight: 400,
                      position: cellPosition,
                      left: leftOffset,
                      minWidth: minCellWidth,
                      zIndex: cellZIndex,
                      backgroundColor: palette.custom.background.primary,
                    }}
                  >
                    {renderCellContent(item, key, type, arrayTruncateLength)}
                  </TableCell>
                );
              })}
            </TableRow>

            {enableRowExpansion &&
              expandable &&
              isExpanded &&
              renderExpandedRow && (
                <TableRow sx={{ backgroundColor: "transparent" }}>
                  <TableCell
                    colSpan={headersArr.length + (enableRowSelection ? 1 : 0)}
                    sx={{ p: 0, borderBottom: 0 }}
                  >
                    {renderExpandedRow(item)}
                  </TableCell>
                </TableRow>
              )}
          </React.Fragment>
        );
      })}
    </TableBody>
  );
};

export default DynamicTableBody;
